/**
 * Data Sources Manifest
 *
 * Static registry of source documents that power DealHub tools.
 * Update this file when documents are added, removed, or refreshed.
 *
 * This is the single source of truth — no database dependency.
 */

export interface DataSourceEntry {
  slug: string
  displayName: string
  filename: string
  description: string
  owner: string
  category: 'kyc_coverage' | 'kyc_sources' | 'kyc_pricing' | 'poc_template' | 'poc_results'
  consumers: Array<{ tool: string; usage: string }>
  versionLabel: string
  lastUpdated: string          // ISO date string
  reviewFrequencyDays: number  // 0 = no scheduled review (e.g. templates, historical)
  filePath: string             // relative to DealHub/docs/
  fileSizeBytes: number
  notes?: string
  documentation?: string       // markdown content describing the data in detail
}

export const DATA_SOURCES_MANIFEST: DataSourceEntry[] = [
  {
    slug: 'kyc-country-coverage',
    displayName: 'FrankieOne Country Coverage',
    filename: 'FrankieOne- Country Coverage-Apr-2026(Internal - verified and tested) (1).xlsx',
    description: 'Full country coverage table (238 countries): what levels of IDV, KYC, KYB, and AML are available per country. Source of truth for scoping POCs and match rate enquiries.',
    owner: 'Vanessa Fierens',
    category: 'kyc_coverage',
    consumers: [
      { tool: 'POC Tool', usage: 'Country picker and coverage validation' },
      { tool: 'Match Rate Enquiry', usage: 'Country coverage data for AI analysis' },
      { tool: 'Scope Tool', usage: 'KYC country selection and coverage checks' },
    ],
    versionLabel: 'May 2026',
    lastUpdated: '2026-05-28',
    reviewFrequencyDays: 90,
    filePath: 'FrankieOne- Country Coverage-Apr-2026(Internal - verified and tested) (1).xlsx',
    fileSizeBytes: 877204,
    documentation: `# FrankieOne Country Coverage

## What this document is

This is the master spreadsheet maintained by Vanessa Fierens that defines FrankieOne's global verification coverage. It is the single source of truth for understanding what identity verification, KYC, KYB, and AML capabilities are available in each country. It is updated quarterly and should always be confirmed with Vanessa before making commitments to customers.

## When to use it

- **Scoping a POC** — check what KYC levels (One_plus, Two_plus, ID match) are available for the customer's target countries before selecting vendors
- **Match Rate Enquiry** — the AI analysis tool uses this data to assess whether FrankieOne can service a customer's country/ruleset requirements
- **Scope creation** — confirm country coverage when filling out the KYC section of a deal scope
- **RFP/RFI responses** — reference for answering coverage questions
- **Customer demos** — show what's available per country

## Sheets we care about for Match Rate analysis

These four sheets are the ones the Match Rate Enquiry and POC tools draw from. The others in the workbook (IDV&Biometrics, KYB Data Coverage, Version Control, etc.) are not currently used.

### Sheet 1: SUMMARY — "Does this country have coverage at all?"

The first filter in any match rate assessment. Lists every country and flags what types of verification are available:

- **Ruleset levels achievable**: 1+, 2+, ID, Basic Profile (defined in the key at the top of the sheet)
- **IDV / Biometrics coverage**: Y/N
- **KYB coverage**: Y/N (and what type)
- **AML coverage**: Y/N

At the moment this sheet only flags yes/no — there's no granularity beyond availability. **Use this first** to rule out countries where we simply don't have coverage.

### Sheet 2: KYC Summary — "What's the high-level picture per country?"

Per-country roll-up that adds quantitative context to SUMMARY:

- **Country** and **Region**
- **Verification types available** (Government ID, KYC)
- **Coverage estimate** — percentage of the country's population represented in the data sources
- **Average match rate** — the typical pass rate we see for that country
- **Response time** — how fast the country's data sources typically respond
- **Verifiable attributes** at a high level — name, DOB, address, national ID

⚠️ **Significant overlap** with SUMMARY and with the KYC sheet. Use for the quantitative fields (coverage %, match rate) rather than the lists of attributes.

### Sheet 3: KYC — ⭐ Most important sheet

The detailed per-data-source breakdown. For every country, for every vendor, for every data source, this sheet tells you:

- **Purpose** — whether the source is for KYC or KYB
- **Source header** — the name of the data source
- **Vendor** — who supplies it (Socure, Data Zoo, GDC, etc.)
- **Government ID** — whether the source verifies Gov IDs
- **Consent required** — whether the data source needs consent
- **Required fields** — what the customer must provide to run a check
- **Mandatory PII** — fields that MUST be supplied to query the source
- **Verifiable elements** — what fields the source CAN verify (the match surface)
- **Optional elements** — fields that can optionally be provided for better matching
- **Adult coverage %** — percentage of the adult population covered by this specific source
- **API testing status** — whether it's been tested end-to-end and which API version (v1/v2/both)
- **National ID details** — format, regex, example (for countries with ID validation)

This is the sheet the Match Rate AI queries most heavily. The mandatory PII tells us if the customer has enough data to even run the check; the verifiable elements tell us what can be matched; the adult coverage predicts pass rates.

### Sheet 4: KYC Recommendations — "Which source should we actually use?"

After the KYC sheet tells us which sources CAN service a request, this sheet tells us which ones we RECOMMEND:

- **Recommended vendor per country**
- **Available vendors** (the full list of options)
- **API status** — Live / Tested / Untested
- **Government ID available** — yes/no at the country level
- **Ad hoc vendor notes** — free-text context about what each data source covers. **This is the valuable field** — nuanced details that don't fit elsewhere.

⚠️ This sheet also contains **pricing**, which we deliberately ignore in the match rate flow. Pricing belongs in a separate pricing/quote process, not in capability assessment.

## Other sheets in the workbook (not currently used)

| Sheet | Purpose |
|-------|---------|
| IDV&Biometrics | Document verification + facial similarity/liveness coverage per country |
| IDV&Biometrics Tested Countries | Countries where IDV has been end-to-end tested |
| Tested countries | Countries where KYC has been end-to-end tested with real data |
| KYB Data Coverage | KYB coverage — UBO/ownership, registry docs, credit reports per country |
| KYC In Build | Sources currently being built or tested — not yet live |
| Data Sources_Summary Table | Summary of all data sources across all vendors |
| Connectors | FrankieOne connector/vendor configuration details |
| Version Control | Change log for the spreadsheet |

## Coverage key

- **IDV (Biometrics)**: Document authenticity + facial similarity / liveness check + OCR
- **KYC One_plus**: At least one data source available for KYC
- **KYC Two_plus**: At least two data sources available for KYC
- **KYC ID**: Government ID validation available (e.g. driver's licence, national ID like AADHAAR)
- **KYB Ownership**: UBO/ownership structure data available
- **AML**: PEPs, Sanctions, and Adverse Media (daily screening)
- \`Y\` = Active | \`O\` = In current roadmap (~4 weeks) | blank = not available / TBD`,
  },
  {
    slug: 'gdc-socure-national-id-address',
    displayName: 'GDC & Socure — National ID and Address Match Details',
    filename: 'GDC and Socure National ID and Address match details.xlsx',
    description: 'Supplementary detail on what national IDs each vendor accepts per country (format, examples, regex) and what address fields each source can actually verify (street, city, zip, state separately). Cross-referenced against field gap analysis.',
    owner: 'Presales',
    category: 'kyc_sources',
    consumers: [
      { tool: 'Match Rate Enquiry', usage: 'Granular national ID format validation and address field verification capabilities per vendor per country' },
      { tool: 'POC Tool', usage: 'Understanding what national IDs are accepted when configuring a POC' },
    ],
    versionLabel: 'Apr 2026',
    lastUpdated: '2026-04-16',
    reviewFrequencyDays: 90,
    filePath: 'GDC and Socure National ID and Address match details.xlsx',
    fileSizeBytes: 258537,
    documentation: `# GDC & Socure — National ID and Address Match Details

## What this document is

A supplementary spreadsheet that provides granular detail on two critical areas that the main Country Coverage sheet only covers at a high level:

1. **Which specific national IDs each vendor accepts per country** — not just "National ID: Yes" but the exact ID types, formats, character counts, regex patterns, and which GDC/Socure sources accept them
2. **What address fields can actually be verified** — not just "Address: Yes" but whether each source can verify street, city, zip code, and state independently

This data is cross-referenced against the vendor field gap analysis reports to flag discrepancies between what vendors claim to verify vs what actually works.

## When to use it

- **Match Rate Enquiry** — when assessing whether a customer's specific national ID type (e.g. HKID, Aadhaar, DNI) will work with a particular vendor
- **POC configuration** — knowing the exact format and character count of national IDs helps validate customer data before running a batch
- **Address verification scoping** — understanding which address components are verified helps set realistic expectations about match rates (e.g. if a source only verifies zip code, address-based matching will be weaker)
- **Vendor comparison** — comparing GDC vs Socure on what they can actually verify per country

## Sheets in the spreadsheet

### Sheet 1: GDC National ID
Per-country list of national ID types that GDC accepts:
- **Country** — the country name
- **National ID Type** — the specific ID (e.g. "Documento Nacional de Identidad (DNI)", "AU Passport", "Foreign Passport")
- **Source Name(s)** — which GDC sources accept this ID (e.g. "GVT1, GVT2", "GVT5")
- **Format Example** — what the ID looks like (e.g. "P1234567", "20-12345678-3")
- **Total Characters** — expected character count including special characters
- **Indicator** — numeric code used by the vendor
- **Comments** — detailed notes on format rules, validation logic, and edge cases

### Sheet 2: Socure National ID
Per-country list of national ID types that Socure accepts:
- **Country Code** and **Country**
- **National ID Name** — the specific ID type
- **Format** — format description (e.g. "7-8 digits (optional dots/dash)")
- **Example** — format example
- **Regex Pattern** — actual regex for validation

Note: This sheet is stored as a markdown table in a single column — needs special parsing.

### Sheet 3: GDC Address
Granular address field verification per GDC source per country:
- **Country** and **GDC Source** (e.g. GVT1, GVT2, CNS1)
- **Type** — source type
- **Adult Coverage** — population coverage percentage
- **Address fields**: Street/House#, City, Zip, State — each individually marked as verifiable or not
- **National ID verifiable/required** — whether national ID can be verified and whether it's mandatory
- **Address Status** — overall address verification assessment
- **Field Gap Analysis** — FM% (field match percentage) and assessment
- **LSEG vs Field Gap Discrepancy** — flags where the vendor's stated capabilities don't match actual field-level testing
- **Corrected Recommendation** — the actual recommendation after resolving discrepancies

Source: LSEG IDV Source Report Nov 2025 cross-referenced with GDC Field Gap Analysis v4 (Mar 2026).

### Sheet 4: Socure Address
Granular address field verification per Socure source per country:
- **Country Code** and **Country**
- **Coverage %** — population coverage
- **Socure Data Source** — specific source name
- **Address fields**: Street, City, Zip, State — each individually marked as verifiable
- **National ID verifiable** and **National ID Type**
- **Can Socure verify Address?** — overall assessment
- **Recommended Action** — what to do with this source for address matching

Source: Socure International Coverage Data v7 (Mar 2026) cross-referenced with Socure Field Gap Analysis v4 (Mar 2026).

## Why this matters

The main Country Coverage sheet often just says "Address: Y" or "National ID: Y" without specifying:
- WHICH national IDs (there can be 5+ per country)
- WHICH address components (some sources only verify zip, not full street)
- WHETHER the vendor actually tested this (field gap analysis flags discrepancies)

This supplementary sheet fills those gaps. Without it, presales might tell a customer "yes we can verify addresses in France" when in reality the source can only verify zip code and state — not street name.`,
  },
]

// Category display labels
export const CATEGORY_LABELS: Record<string, string> = {
  kyc_coverage: 'KYC Coverage',
  kyc_sources: 'KYC Sources',
  kyc_pricing: 'KYC Pricing',
  poc_template: 'POC Template',
  poc_results: 'POC Results',
}

// Freshness calculation
export type Freshness = 'fresh' | 'due_soon' | 'overdue' | 'no_review'

export function getFreshness(entry: DataSourceEntry): Freshness {
  if (entry.reviewFrequencyDays === 0) return 'no_review'
  const lastUpdated = new Date(entry.lastUpdated).getTime()
  const nextDue = lastUpdated + entry.reviewFrequencyDays * 86400000
  const now = Date.now()
  const warningMs = 14 * 86400000
  if (now > nextDue) return 'overdue'
  if (now > nextDue - warningMs) return 'due_soon'
  return 'fresh'
}

export function getNextReviewDate(entry: DataSourceEntry): string | null {
  if (entry.reviewFrequencyDays === 0) return null
  const lastUpdated = new Date(entry.lastUpdated).getTime()
  return new Date(lastUpdated + entry.reviewFrequencyDays * 86400000).toISOString().split('T')[0]
}
